import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "../lib/supabaseClient";
import { generateTransmissionDossierPDF } from "../lib/TransmissionDossier";
import { generateTransmissionDossierXLSX } from "../lib/TransmissionDossierXlsx";
import { VISION_URL } from "../config/domains";
import { v4 as uuidv4 } from "uuid";
import {
  Users,
  Eye,
  Sparkles,
  TrendingUp,
  BookOpen,
  Award,
  Plus,
  Pencil,
  Link2,
  ChevronRight,
  CheckCircle,
  X,
  Mail,
  AlertCircle,
  Image as ImageIcon,
  Scale,
  FileDown,
  FileSpreadsheet,
  KeyRound,
  Palette,
  Clock,
  Wallet,
  Heart,
  UserRound,
  Images,
} from "lucide-react";
import { useTranslation } from "react-i18next";

interface UserProfile {
  id: string;
  first_name: string;
  role: "user" | "family_office" | "admin" | "expert";
  plan: "standard" | "premium" | "family_office_plan";
}

interface Beneficiary {
  id: string;
  name: string;
  relation: string;
  email?: string;
  image_url?: string;
  phone?: string;
}

interface Artwork {
  id: string;
  title: string;
  artist: string;
  year?: number;
  image_url?: string;
  images?: any;
  price?: number;
  value?: number;
}

interface ArtworkMapping {
  artwork_id: string;
  beneficiary_id: string;
}

/**
 * Ligne de la vue `heir_questionnaire_progress` — SOURCE UNIQUE de complétude,
 * calculée sur les vraies réponses (heir_questionnaire_responses).
 *
 * ⚠️ NE PAS revenir à `heir_questionnaires.completion_status` : rien ne le passe
 * jamais à "completed" (les seules écritures mettent "in_progress"), donc un
 * héritier ayant terminé restait affiché « En cours ». Audit 16/07/2026.
 */
interface HeirQuestionnaire {
  beneficiary_id: string;
  responses_count: number;
  completion_percentage: number;
}

const QUESTIONS_REQUIRED = 32;

/**
 * Seuil indicatif de concentration au-delà duquel on invite à vérifier la
 * réserve héréditaire. Paramétrable — ce n'est PAS un calcul légal.
 */
const HEIR_CONCENTRATION_ALERT_PCT = 50;

/**
 * Barèmes des droits de succession (France, 2024-2025) — STRICTEMENT INDICATIF.
 * ⚠️ L'abattement est propre à chaque héritier et s'apprécie sur la TOTALITÉ de
 * ce qu'il reçoit (tous actifs confondus), pas sur la seule part artistique :
 * l'estimation ci-dessous n'est donc qu'un ordre de grandeur, à confirmer par
 * le notaire. Barème progressif par tranches cumulées.
 */
type TaxBracket = { upTo: number; rate: number };
type TaxRegimeKey =
  | "directLine"
  | "spouse"
  | "sibling"
  | "nephew"
  | "institution"
  | "other";

const TAX_REGIMES: Record<
  TaxRegimeKey,
  { abattement: number; brackets: TaxBracket[]; exempt?: boolean }
> = {
  directLine: {
    abattement: 100000,
    brackets: [
      { upTo: 8072, rate: 0.05 },
      { upTo: 12109, rate: 0.1 },
      { upTo: 15932, rate: 0.15 },
      { upTo: 552324, rate: 0.2 },
      { upTo: 902838, rate: 0.3 },
      { upTo: 1805677, rate: 0.4 },
      { upTo: Infinity, rate: 0.45 },
    ],
  },
  spouse: { abattement: 0, brackets: [], exempt: true },
  sibling: {
    abattement: 15932,
    brackets: [
      { upTo: 24430, rate: 0.35 },
      { upTo: Infinity, rate: 0.45 },
    ],
  },
  nephew: { abattement: 7967, brackets: [{ upTo: Infinity, rate: 0.55 }] },
  institution: { abattement: 0, brackets: [], exempt: true },
  other: { abattement: 1594, brackets: [{ upTo: Infinity, rate: 0.6 }] },
};

/** Devine le régime fiscal d'après la relation (texte libre possible). */
const inferTaxRegime = (relation?: string): TaxRegimeKey => {
  const r = (relation || "").toLowerCase();
  if (/conjoint|époux|epoux|épouse|epouse|mari|femme|pacs/.test(r)) return "spouse";
  if (/frère|frere|sœur|soeur/.test(r)) return "sibling";
  if (/neveu|nièce|niece/.test(r)) return "nephew";
  if (/fondation|institution|association|musée|musee|fonds/.test(r))
    return "institution";
  if (/fils|fille|enfant|descendant|petit-fils|petite-fille|famille/.test(r))
    return "directLine";
  return "other";
};

/** Droits de succession estimés sur une base, par tranches cumulées. */
const estimateInheritanceTax = (base: number, key: TaxRegimeKey): number => {
  const reg = TAX_REGIMES[key];
  if (reg.exempt) return 0;
  let taxable = Math.max(base - reg.abattement, 0);
  let tax = 0;
  let prev = 0;
  for (const b of reg.brackets) {
    if (taxable <= 0) break;
    const span = Math.min(taxable, b.upTo - prev);
    if (span > 0) {
      tax += span * b.rate;
      taxable -= span;
    }
    prev = b.upTo;
  }
  return Math.round(tax);
};

/** Règle de complétude commune à toute la page. */
const isQuestionnaireDone = (q?: { responses_count?: number | null } | null) =>
  Number(q?.responses_count ?? 0) >= QUESTIONS_REQUIRED;

interface Estimation {
  artwork_id: string;
  estimated_value: number;
  created_at: string;
  estimation_date?: string | null;
  date?: string | null;
  method?: string | null;
}

/** Origine de la valeur affichée pour une œuvre (crédibilité notariale). */
type ValuationSource = "estimation" | "declared" | "purchase" | "none";

interface Valuation {
  value: number;
  date: string | null;
  source: ValuationSource;
}

// ---------------------------------------------------------------------------
// Badge
// ---------------------------------------------------------------------------
function Badge({
  label,
  tone = "gray",
  title,
  icon: Icon,
}: {
  label: string;
  tone?: "green" | "amber" | "red" | "gray" | "gold";
  title?: string;
  icon?: React.ComponentType<{ className?: string }>;
}) {
  const tones: Record<string, string> = {
    green: "bg-emerald-50 text-emerald-800 border-emerald-200",
    amber: "bg-amber-50 text-amber-800 border-amber-200",
    red: "bg-red-50 text-red-800 border-red-200",
    gray: "bg-gray-100 text-gray-700 border-gray-200",
    gold: "bg-[#faf6ec] text-[#8a6a2f] border-[#e6d9b3]",
  };
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-medium leading-none whitespace-nowrap ${
        tones[tone] || tones.gray
      }`}
      title={title}
    >
      {Icon ? (
        <Icon className="h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
      ) : (
        <span className="inline-block h-1.5 w-1.5 rounded-full bg-current/60" aria-hidden="true" />
      )}
      {label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Statut héritier (i18n)
// ---------------------------------------------------------------------------
function getHeirStatus(
  heir: { id: string },
  questionnaires: HeirQuestionnaire[] | undefined,
  t: (key: string) => string
): {
  label: string;
  tone: "green" | "amber" | "red" | "gray";
  icon: React.ComponentType<{ className?: string }>;
} {
  const q = (questionnaires ?? []).find((x) => x.beneficiary_id === heir.id);
  const answered = Number(q?.responses_count ?? 0);

  if (answered >= QUESTIONS_REQUIRED) {
    return { label: t("succession.heirStatus.completed"), tone: "green", icon: CheckCircle };
  }
  if (answered > 0) {
    return { label: t("succession.heirStatus.inProgress"), tone: "amber", icon: Clock };
  }
  return { label: t("succession.heirStatus.pending"), tone: "gray", icon: AlertCircle };
}

// ---------------------------------------------------------------------------
// Badge de relation (icône selon le type de lien)
// ---------------------------------------------------------------------------
function getRelationIcon(relation: string): React.ComponentType<{ className?: string }> {
  const r = (relation || "").toLowerCase();
  if (r.includes("conjoint") || r.includes("spouse") || r.includes("partner")) return Heart;
  if (r.includes("famille") || r.includes("family")) return Users;
  return UserRound;
}

// ---------------------------------------------------------------------------

export default function PlanificationSuccessorale() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const currentLocale = i18n.language || "fr";

  // Mode délégué : un family office / advisor avec collection_permissions.can_edit
  // peut consulter et compléter le dossier successoral d'un client via
  // /succession?clientId=<id>. Sans ce paramètre, comportement inchangé (soi-même).
  const [searchParams] = useSearchParams();
  const delegateClientId = searchParams.get("clientId");
  const [delegateClientName, setDelegateClientName] = useState<string>("");

  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [beneficiaries, setBeneficiaries] = useState<Beneficiary[]>([]);
  const [artworks, setArtworks] = useState<Artwork[]>([]);
  const [mappings, setMappings] = useState<ArtworkMapping[]>([]);
  const [estimations, setEstimations] = useState<Estimation[]>([]);
  const [questionnaires, setQuestionnaires] = useState<HeirQuestionnaire[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>("");
  const [sendingEmail, setSendingEmail] = useState(false);

  /**
   * Patrimoine GLOBAL (titres, immobilier, liquidités…). La réserve héréditaire
   * ne s'apprécie jamais sur la seule collection : à terme, on renseigne cette
   * base depuis l'API du family office. Tant qu'elle n'est pas branchée, les
   * parts sont calculées sur la seule valeur de la collection (signalé à l'UI).
   */
  const [externalEstate, setExternalEstate] = useState<{
    totalValue: number;
    source: string;
    asOf?: string;
  } | null>(null);

  const [exportingPdf, setExportingPdf] = useState(false);
  const [exportingXls, setExportingXls] = useState(false);

  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState<Beneficiary | null>(null);

  // Modales
  const [showAddModal, setShowAddModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedBeneficiary, setSelectedBeneficiary] = useState<Beneficiary | null>(null);
  const [newBenef, setNewBenef] = useState({ name: "", relation: "Famille", email: "" });

  const openEdit = (b: Beneficiary) => {
    setEditForm({
      ...b,
      phone: b.phone ?? "",
    });
    setShowEditModal(true);
  };

  // -------------------------------------------------------------------------
  // Chargement données Supabase
  // -------------------------------------------------------------------------
  const fetchUserData = useCallback(async () => {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) {
        navigate("/login");
        return;
      }

      // Profil — celui du compte connecté (rôle/plan), pas celui du client visité
      // en mode délégué : les vérifications d'autorisation se font côté RPC/RLS,
      // ce champ ne sert qu'à l'affichage ("vous êtes...").
      const { data: profile } = await supabase
        .from("profiles")
        .select("id, first_name, role, plan")
        .eq("id", user.id)
        .single();

      setUserProfile(profile);

      const targetId = delegateClientId || user.id;

      if (delegateClientId) {
        const { data: targetProfile } = await supabase
          .from("profiles")
          .select("first_name, last_name, company")
          .eq("id", targetId)
          .maybeSingle();
        setDelegateClientName(
          [targetProfile?.first_name, targetProfile?.last_name].filter(Boolean).join(" ") ||
            targetProfile?.company ||
            "ce client"
        );
      }

const [
  { data: beneficiariesData, error: beneficiariesErr },
  { data: artworksData, error: artworksErr },
  { data: mappingsData, error: mappingsErr },
  { data: estimationsData, error: estimErr },
] = await Promise.all([
  supabase
    .from("beneficiaries")
    .select('id, name, relation, email, art_styles, emotions')
    .eq('client_id', targetId),

  supabase
    .from("artworks")
    .select("id, title, artist, year, image_url, images")
    .eq("client_id", targetId),

  supabase
    .from("artwork_beneficiary")
    .select("artwork_id, beneficiary_id")
    .eq("client_id", targetId),

  supabase
    .from("estimations")
    .select("*")
    .eq("client_id", targetId),
]);
const beneficiaryIds = (beneficiariesData ?? []).map(b => b.id);

let questionnairesData: any[] = [];
let qErr = null;

if (beneficiaryIds.length > 0) {
  // Vue calculée sur les vraies réponses (cf. interface HeirQuestionnaire ci-dessus).
  const res = await supabase
    .from("heir_questionnaire_progress")
    .select("beneficiary_id, responses_count, completion_percentage")
    .in("beneficiary_id", beneficiaryIds);

  questionnairesData = res.data ?? [];
  qErr = res.error;
}
      if (beneficiariesErr || artworksErr || mappingsErr || estimErr) {
        console.error("❌ Supabase critical errors:", {
          beneficiariesErr,
          artworksErr,
          mappingsErr,
          estimErr,
        });
      }
      if (qErr) {
        console.warn("⚠️ heir_questionnaire_progress non bloquant:", qErr);
      }

      setBeneficiaries(beneficiariesData || []);
      setArtworks(artworksData || []);
      setMappings(mappingsData || []);
      setEstimations(estimationsData || []);
      setQuestionnaires(questionnairesData || []);
    } catch (err) {
      console.error("Erreur lors du chargement des données:", err);
      setError("load");
    } finally {
      setLoading(false);
    }
  }, [navigate, delegateClientId]);

  useEffect(() => {
    fetchUserData();
  }, [fetchUserData]);

  // -------------------------------------------------------------------------
  // Point de branchement API family office (patrimoine global).
  // Contrat attendu : { totalValue: number, source: string, asOf?: string }.
  // No-op tant que REACT_APP_FO_ESTATE_API n'est pas défini : on retombe alors
  // sur la valeur de la collection comme base de calcul des parts.
  // -------------------------------------------------------------------------
  useEffect(() => {
    const foApi = process.env.REACT_APP_FO_ESTATE_API;
    if (!foApi) return;
    let cancelled = false;
    (async () => {
      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();
        const res = await fetch(foApi, {
          headers: session?.access_token
            ? { Authorization: `Bearer ${session.access_token}` }
            : {},
        });
        if (!res.ok) return;
        const j = await res.json();
        if (!cancelled && typeof j?.totalValue === "number") {
          setExternalEstate({
            totalValue: j.totalValue,
            source: j.source || "Family Office",
            asOf: j.asOf,
          });
        }
      } catch (e) {
        console.warn("API patrimoine family office indisponible:", e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // -------------------------------------------------------------------------
  // Image d'une œuvre
  // -------------------------------------------------------------------------
  const getArtImageUrl = (artwork: Artwork): string => {
    if (artwork.images) {
      try {
        let imagesData = artwork.images;
        if (typeof imagesData === "string") {
          imagesData = JSON.parse(imagesData);
        }

        if (Array.isArray(imagesData)) {
          const primaryImage = imagesData.find((img) => img.isPrimary === true);
          if (primaryImage?.url || primaryImage?.path) {
            return primaryImage.url || primaryImage.path;
          }
          if (imagesData.length > 0 && (imagesData[0].url || imagesData[0].path)) {
            return imagesData[0].url || imagesData[0].path;
          }
        } else if (imagesData.url || imagesData.path) {
          return imagesData.url || imagesData.path;
        }
      } catch (error) {
        console.error("Erreur lors du traitement du champ images:", error);
      }
    }

    if (artwork.image_url) {
      if (
        artwork.image_url.startsWith("http://") ||
        artwork.image_url.startsWith("https://")
      ) {
        return artwork.image_url;
      }
      try {
        const { data } = supabase.storage
          .from("artworks-images")
          .getPublicUrl(artwork.image_url);
        return data.publicUrl;
      } catch (error) {
        console.error("Erreur lors de la récupération de l'URL d'image:", error);
      }
    }

    return "/images/artworks/Vignette_oeuvre_Image_Not_Available.png";
  };

  // -------------------------------------------------------------------------
  // Ajouter un bénéficiaire
  // -------------------------------------------------------------------------
  const addBeneficiaire = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const clientId = delegateClientId || session?.user?.id;
      if (!clientId) throw new Error("Session expirée");

      // Écriture via RPC (SECURITY DEFINER) plutôt qu'un insert direct : elle
      // vérifie l'autorisation (soi-même OU délégué avec collection_permissions
      // .can_edit sur ce client) et crée le plan de succession au premier
      // héritier de façon atomique — voir migration allow_delegate_add_beneficiary.
      const { error: rpcErr } = await supabase.rpc("add_beneficiary_for_client", {
        p_client_id: clientId,
        p_name: newBenef.name,
        p_relation: newBenef.relation,
        p_email: newBenef.email || null,
      });

      if (rpcErr) throw rpcErr;

      setNewBenef({ name: "", relation: "Famille", email: "" });
      setShowAddModal(false);
      await fetchUserData();
    } catch (err: any) {
      console.error(err);
      setError("add");
    }
  };

  // -------------------------------------------------------------------------
  // Attribution / retrait d'œuvre
  // -------------------------------------------------------------------------
  const assignArtworkTo = async (artworkId: string) => {
    if (!selectedBeneficiary) return;
    setError("");
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const clientId = delegateClientId || session?.user?.id;

      const { error: insErr } = await supabase.from("artwork_beneficiary").insert([
        {
          artwork_id: artworkId,
          beneficiary_id: selectedBeneficiary.id,
          client_id: clientId,
        },
      ]);

      if (insErr) throw insErr;
      await fetchUserData();
    } catch (err: any) {
      console.error(err);
      setError("assign");
    }
  };

  const removeAssignment = async (artworkId: string) => {
    if (!selectedBeneficiary) return;
    setError("");
    try {
      const { error: delErr } = await supabase
        .from("artwork_beneficiary")
        .delete()
        .match({ artwork_id: artworkId, beneficiary_id: selectedBeneficiary.id });

      if (delErr) throw delErr;
      await fetchUserData();
    } catch (err: any) {
      console.error(err);
      setError("remove");
    }
  };

  // -------------------------------------------------------------------------
  // Lien questionnaire héritier
  // -------------------------------------------------------------------------

const VISION_PUBLIC_URL = VISION_URL;

const buildQuestionnaireUrlFromToken = (token: string) =>
  `${VISION_PUBLIC_URL}/matching-intergenerationnel/questionnaire?token=${encodeURIComponent(token)}`;

const openQuestionnaireLink = async (beneficiary: Beneficiary) => {
  try {
    if (!beneficiary?.id) throw new Error("missing_beneficiary");
    const token = await getOrCreateAccessToken(beneficiary.id);
    const url = buildQuestionnaireUrlFromToken(token);
    window.open(url, "_blank", "noopener,noreferrer");
  } catch (e) {
    console.error(e);
    alert(t("succession.error.copyLink"));
  }
};

  const copyQuestionnaireLink = async (beneficiary: Beneficiary) => {
    try {
      if (!beneficiary?.id) throw new Error("missing_beneficiary");
      const token = await getOrCreateAccessToken(beneficiary.id);
      const url = buildQuestionnaireUrlFromToken(token);

      let copied = false;

      // Tentative 1 : Clipboard API moderne
      if (navigator?.clipboard?.writeText) {
        try {
          await navigator.clipboard.writeText(url);
          copied = true;
        } catch (_) {
          // bloqué sur Android sans geste utilisateur direct
        }
      }

      // Tentative 2 : execCommand (fallback)
      if (!copied) {
        try {
          const ta = document.createElement("textarea");
          ta.value = url;
          ta.style.position = "fixed";
          ta.style.top = "0";
          ta.style.left = "0";
          ta.style.opacity = "0";
          document.body.appendChild(ta);
          ta.focus();
          ta.select();
          copied = document.execCommand("copy");
          document.body.removeChild(ta);
        } catch (_) {}
      }

      if (copied) {
        alert(t("succession.alert.linkCopied"));
      } else {
        // Fallback ultime : afficher le lien pour copie manuelle
        alert(t("succession.error.copyLink") + "\n\n" + url);
      }
    } catch (e: any) {
      console.error(e);
      alert(t("succession.error.copyLink"));
    }
  };

  // -------------------------------------------------------------------------
  // Invitation compte héritier (distinct du lien questionnaire ci-dessus :
  // celui-ci crée un vrai compte ARTKIV — role "heir" — via
  // create_beneficiary_invite + la page /heirs/register).
  // -------------------------------------------------------------------------
  const [creatingInviteId, setCreatingInviteId] = useState<string | null>(null);

  const copyHeirAccountLink = async (beneficiary: Beneficiary) => {
    if (!beneficiary?.id) return;
    setCreatingInviteId(beneficiary.id);
    try {
      const { data, error } = await supabase.rpc("create_beneficiary_invite", {
        p_beneficiary_id: beneficiary.id,
      });
      if (error) throw error;

      const row = Array.isArray(data) ? data[0] : data;
      const token = row?.invite_token;
      if (!token) throw new Error("missing_token");

      const url = `${window.location.origin}/heirs/register?token=${encodeURIComponent(token)}`;

      let copied = false;
      if (navigator?.clipboard?.writeText) {
        try {
          await navigator.clipboard.writeText(url);
          copied = true;
        } catch (_) {}
      }
      if (!copied) {
        try {
          const ta = document.createElement("textarea");
          ta.value = url;
          ta.style.position = "fixed";
          ta.style.opacity = "0";
          document.body.appendChild(ta);
          ta.focus();
          ta.select();
          copied = document.execCommand("copy");
          document.body.removeChild(ta);
        } catch (_) {}
      }

      alert(
        copied
          ? t("succession.alert.heirAccountLinkCopied", {
              defaultValue: "Lien d'accès compte copié — envoyez-le à {{name}}.",
              name: beneficiary.name,
            })
          : t("succession.alert.heirAccountLinkFallback", {
              defaultValue: "Copiez ce lien manuellement :",
            }) + "\n\n" + url
      );
    } catch (e: any) {
      console.error("Erreur création invitation héritier:", e);
      alert(
        t("succession.error.heirInvite", {
          defaultValue: "Impossible de créer le lien d'accès compte.",
        })
      );
    } finally {
      setCreatingInviteId(null);
    }
  };

const getOrCreateAccessToken = async (
  beneficiaryId: string,
  forceNew = false
): Promise<string> => {
  // Si pas forceNew, tenter de récupérer un token valide existant
  if (!forceNew) {
    const { data: existing } = await supabase
      .from("beneficiaries_access")
      .select("access_token, expires_at, created_at")
      .eq("beneficiary_id", beneficiaryId)
      .gt("expires_at", new Date().toISOString())
      .order("created_at", { ascending: false })
      .limit(1);

    if (existing && existing.length > 0) {
      return existing[0].access_token as string;
    }
  }

  // Upsert : crée ou remplace le token (évite le 409 sur contrainte UNIQUE beneficiary_id)
  const newToken = uuidv4();
  const newExpiry = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString();

  const { data: upserted, error: upsertErr } = await supabase
    .from("beneficiaries_access")
    .upsert(
      {
        beneficiary_id: beneficiaryId,
        access_token: newToken,
        expires_at: newExpiry,
      },
      { onConflict: "beneficiary_id" }
    )
    .select("access_token")
    .single();

  if (upsertErr) throw upsertErr;
  return upserted!.access_token as string;
};



  const sendQuestionnaire = async (beneficiary: Beneficiary) => {
    if (!beneficiary.email) {
      alert(t("succession.error.noEmail"));
      return;
    }

    const isCompleted =
      questionnaires?.some(
        (q) => q.beneficiary_id === beneficiary.id && isQuestionnaireDone(q)
      ) ?? false;

    if (isCompleted) {
      alert(t("succession.alert.questionnaireAlreadyDone"));
      return;
    }

    setSendingEmail(true);
    setError("");

    try {
      const uuid =
        typeof window !== "undefined" && window.crypto && "randomUUID" in window.crypto
          ? window.crypto.randomUUID()
          : Math.random().toString(36).slice(2) + Date.now().toString(36);

      const token = await getOrCreateAccessToken(beneficiary.id, true);
      const questionnaireUrl = buildQuestionnaireUrlFromToken(token);


      const { data: { session: currentSession } } = await supabase.auth.getSession();
      const accessToken = currentSession?.access_token;

      const res = await fetch(
        "https://hofuztrcrvjihagsqdqb.supabase.co/functions/v1/send-heir-questionnaire-email",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
          },
          body: JSON.stringify({
            email: beneficiary.email,
            name: beneficiary.name,
            token,
            uuid,
            questionnaireUrl,
          }),
        }
      );

      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j?.error || "send_failed");
      }

      alert(
        t("succession.alert.questionnaireSent", {
          name: beneficiary.name,
          email: beneficiary.email,
        })
      );
    } catch (err: any) {
      console.error("Erreur lors de l'envoi du questionnaire:", err);
      setError("send");
      alert(t("succession.error.sendQuestionnaire"));
    } finally {
      setSendingEmail(false);
    }
  };

  // -------------------------------------------------------------------------
  // Helpers valeurs
  // -------------------------------------------------------------------------
  const unassignedArtworks = artworks.filter(
    (a) => !mappings.some((m) => m.artwork_id === a.id)
  );

  const getAssignedArtworks = (beneficiaryId: string) => {
    const assignedIds = mappings
      .filter((m) => m.beneficiary_id === beneficiaryId)
      .map((m) => m.artwork_id);
    return artworks.filter((a) => assignedIds.includes(a.id));
  };

  const getCompletedQuestionnaires = () => questionnaires.filter(isQuestionnaireDone).length;

  const saveBeneficiary = async (updated: Beneficiary) => {
    try {
      setError("");

      const {
        data: { session },
      } = await supabase.auth.getSession();
      const userId = session?.user?.id;
      if (!userId) {
        alert(t("succession.edit.sessionExpired"));
        return;
      }

      // ⚠️ Ne PAS faire de .from("beneficiaries").update() direct : il n'existe aucune
      // policy RLS UPDATE sur cette table (par choix — les champs calculés du questionnaire
      // score_*/dim_*/profile_calculated_at ne doivent pas être modifiables depuis le front).
      // L'écriture passe par la RPC SECURITY DEFINER update_beneficiary_contact_info, qui
      // vérifie explicitement client_id = auth.uid() et ne touche que les 4 champs de contact.
      const { data: rpcData, error: updErr } = await supabase.rpc(
        "update_beneficiary_contact_info",
        {
          p_beneficiary_id: updated.id,
          p_name: updated.name,
          p_relation: updated.relation,
          p_email: updated.email,
          p_phone: updated.phone ?? "",
        }
      );

      if (updErr) throw updErr;

      let row = rpcData
        ? {
            id: rpcData.id,
            name: rpcData.name,
            relation: rpcData.relation,
            email: rpcData.email,
            image_url: rpcData.image_url ?? updated.image_url ?? null,
            phone: rpcData.phone,
          }
        : null;
      if (!row) {
        const { data: selData, error: selErr } = await supabase
          .from("beneficiaries")
          .select("id, name, relation, email, image_url, phone")
          .match({ id: updated.id, client_id: userId })
          .maybeSingle();

        if (selErr) throw selErr;
        row = selData || null;
      }

      if (!row) {
        setBeneficiaries((prev) =>
          prev.map((b) => (b.id === updated.id ? { ...b, ...updated } : b))
        );
        alert(t("succession.edit.updatedLimited"));
        return;
      }

      setBeneficiaries((prev) =>
        prev.map((b) => (b.id === updated.id ? { ...b, ...row } : b))
      );
      alert(t("succession.edit.updated"));
    } catch (e: any) {
      console.error(e);
      alert(t("succession.edit.error"));
    }
  };

  // Date de référence d'une estimation (estimation_date > date > created_at).
  const estimationDate = (e: Estimation) =>
    e.estimation_date || e.date || e.created_at || null;

  /**
   * Valorisation d'une œuvre AVEC sa date et son origine — indispensable pour
   * un notaire : la succession retient la valeur vénale au jour du décès, une
   * valeur non datée n'a pas de portée juridique.
   */
  const getArtworkValuation = (artwork: Artwork): Valuation => {
    const rows = estimations.filter((e) => e.artwork_id === artwork.id);
    if (rows.length > 0) {
      const latest = [...rows].sort(
        (a, b) =>
          new Date(estimationDate(b) ?? 0).getTime() -
          new Date(estimationDate(a) ?? 0).getTime()
      )[0];
      return {
        value: Number(latest.estimated_value) || 0,
        date: estimationDate(latest),
        source: "estimation",
      };
    }
    if (artwork.value) return { value: artwork.value, date: null, source: "declared" };
    if (artwork.price) return { value: artwork.price, date: null, source: "purchase" };
    return { value: 0, date: null, source: "none" };
  };

  const getArtworkValue = (artwork: Artwork) => getArtworkValuation(artwork).value;

  // Libellé i18n de l'origine + date formatée pour l'UI.
  const valuationSourceLabel = (s: ValuationSource) =>
    t(`succession.valuation.source.${s}`);
  const fmtDate = (d: string | null) =>
    d
      ? new Date(d).toLocaleDateString(currentLocale === "fr" ? "fr-FR" : "en-US")
      : null;
  /** Rend « Estimation · au 12/03/2026 » ou « Prix d'achat · non datée ». */
  const valuationCaption = (v: Valuation) => {
    const src = valuationSourceLabel(v.source);
    if (v.source === "none") return src;
    const d = fmtDate(v.date);
    return `${src} · ${
      d ? t("succession.valuation.asOf", { date: d }) : t("succession.valuation.undated")
    }`;
  };

  // -------------------------------------------------------------------------
  // Contenu selon plan (i18n)
  // -------------------------------------------------------------------------
  const getPlanSpecificContent = () => {
    if (!userProfile) return null;

    const planType =
      userProfile.role === "family_office" ? "family_office_plan" : userProfile.plan;
    const completedCount = getCompletedQuestionnaires();

    switch (planType) {
      case "standard":
        return (
          <div className="space-y-6">
            {/* Bloc attribution manuelle */}
            <div className="rounded-2xl p-8" style={{ background: "#faf6ec", border: "1px solid #e6d9b3" }}>
              <div className="flex items-start gap-4">
                <div className="p-3 rounded-xl shadow-sm" style={{ background: "#fff" }}>
                  <BookOpen className="h-6 w-6" style={{ color: "#a9843f" }} />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    {t("succession.standard.title")}
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    {t("succession.standard.text")}
                  </p>
                </div>
              </div>
            </div>

            {/* Bloc Premium / vision.artkiv */}
            <div className="rounded-2xl overflow-hidden" style={{ background: "#faf6ec", border: "1px solid #e6d9b3" }}>
              <div className="p-8">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-3 rounded-xl shadow-sm" style={{ background: "#fff" }}>
                    <Sparkles className="h-7 w-7" style={{ color: "#a9843f" }} />
                  </div>
                  <div>
                    <h3 className="btn-artsy btn-primary-artsy">
                      {t("succession.standard.premiumTeaser.title")}
                    </h3>
                    <p className="font-medium" style={{ color: "#a9843f" }}>
                      {t("succession.standard.premiumTeaser.subtitle")}
                    </p>
                  </div>
                </div>

                <div className="space-y-4 mb-6">
                  <p className="text-gray-700 leading-relaxed">
                    {t("succession.standard.premiumTeaser.paragraph")}
                  </p>

                  <div className="grid md:grid-cols-3 gap-4">
                    <div className="rounded-xl p-4" style={{ background: "#fff", border: "1px solid #e6d9b3" }}>
                      <div className="text-3xl font-bold mb-1" style={{ color: "#a9843f" }}>
                        92%
                      </div>
                      <div className="text-sm text-gray-700">
                        {t("succession.standard.premiumTeaser.stat1.label")}
                      </div>
                    </div>
                    <div className="rounded-xl p-4" style={{ background: "#fff", border: "1px solid #e6d9b3" }}>
                      <div className="text-3xl font-bold mb-1" style={{ color: "#a9843f" }}>
                        Art Market
                      </div>
                      <div className="text-sm text-gray-700">
                        {t("succession.standard.premiumTeaser.stat2.label")}
                      </div>
                    </div>
                    <div className="rounded-xl p-4" style={{ background: "#fff", border: "1px solid #e6d9b3" }}>
                      <div className="text-3xl font-bold mb-1" style={{ color: "#a9843f" }}>
                        15+
                      </div>
                      <div className="text-sm text-gray-700">
                        {t("succession.standard.premiumTeaser.stat3.label")}
                      </div>
                    </div>
                  </div>

                  <div className="rounded-xl p-5" style={{ background: "#fff", border: "1px solid #e6d9b3" }}>
                    <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <Award className="h-5 w-5" style={{ color: "#a9843f" }} />
                      {t("succession.standard.premiumTeaser.sectionTitle")}
                    </h4>
                    <ul className="space-y-2 text-gray-700">
                      <li className="flex items-start gap-2">
                        <ChevronRight className="h-5 w-5 flex-shrink-0 mt-0.5" style={{ color: "#a9843f" }} />
                        <span>{t("succession.standard.premiumTeaser.point1")}</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <ChevronRight className="h-5 w-5 flex-shrink-0 mt-0.5" style={{ color: "#a9843f" }} />
                        <span>{t("succession.standard.premiumTeaser.point2")}</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <ChevronRight className="h-5 w-5 flex-shrink-0 mt-0.5" style={{ color: "#a9843f" }} />
                        <span>{t("succession.standard.premiumTeaser.point3")}</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <ChevronRight className="h-5 w-5 flex-shrink-0 mt-0.5" style={{ color: "#a9843f" }} />
                        <span>{t("succession.standard.premiumTeaser.point4")}</span>
                      </li>
                    </ul>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    onClick={() => navigate("/upgrade")}
                    className="flex-1 px-6 py-3 text-white rounded-xl font-semibold transition-all duration-200 flex items-center justify-center gap-2"
                    style={{ background: "#1a1a1a" }}
                  >
                    <Sparkles className="h-5 w-5" />
                    {t("succession.standard.premiumTeaser.button")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        );

      case "premium":
        return (
          <div className="rounded-2xl p-6 sm:p-8 shadow-lg" style={{ background: "#1a1a1a" }}>
            <div className="flex items-start gap-4 mb-6">
              <div className="p-3 rounded-xl flex-shrink-0" style={{ background: "rgba(201,169,97,0.18)" }}>
                <Sparkles className="h-6 w-6" style={{ color: "#c9a961" }} />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-xl font-semibold mb-2" style={{ color: "#f9f7f4" }}>
                  {t("succession.premium.title")}
                </h3>
                <p className="leading-relaxed" style={{ color: "rgba(249,247,244,0.75)" }}>
                  {t("succession.premium.text", {
                    count: completedCount,
                  })}
                </p>
              </div>
            </div>
            <button
              onClick={() => window.open(`${VISION_URL}/transmission`, "_blank")}
              className="w-full px-6 py-3 rounded-xl font-semibold transition-all duration-200 flex items-center justify-center gap-2 shadow-md"
              style={{ background: "#c9a961", color: "#1a1a1a" }}
            >
              <Sparkles className="h-5 w-5" />
              {t("succession.premium.button")}
            </button>
          </div>
        );

      default:
        return null;
    }
  };

  // -------------------------------------------------------------------------
  // Loading
  // -------------------------------------------------------------------------
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#f9f7f4" }}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 mx-auto mb-4" style={{ border: "2px solid #e5e0d8", borderBottomColor: "#c9a961" }}></div>
          <p style={{ color: "#6b6459" }}>{t("succession.loading")}</p>
        </div>
      </div>
    );
  }

  // -------------------------------------------------------------------------
  // Équilibre de la transmission (synthèse notaire / family office)
  // -------------------------------------------------------------------------
  const collectionValue = artworks.reduce((s, a) => s + getArtworkValue(a), 0);
  const assignedArtworkIds = new Set(mappings.map((m) => m.artwork_id));
  const assignedValue = artworks
    .filter((a) => assignedArtworkIds.has(a.id))
    .reduce((s, a) => s + getArtworkValue(a), 0);
  const unassignedValue = Math.max(collectionValue - assignedValue, 0);
  const coveragePct =
    collectionValue > 0 ? Math.round((assignedValue / collectionValue) * 100) : 0;

  // Base des parts : patrimoine global si l'API FO est branchée, sinon collection.
  const estateBase = externalEstate?.totalValue ?? collectionValue;

  const heirShares = beneficiaries
    .map((b) => {
      const value = getAssignedArtworks(b.id).reduce(
        (s, a) => s + getArtworkValue(a),
        0
      );
      const pct = estateBase > 0 ? Math.round((value / estateBase) * 100) : 0;
      const regime = inferTaxRegime(b.relation);
      const tax = estimateInheritanceTax(value, regime);
      return { id: b.id, name: b.name, value, pct, regime, tax };
    })
    .sort((a, b) => b.value - a.value);

  const maxHeirPct = heirShares.reduce((m, h) => Math.max(m, h.pct), 0);
  const concentrationWarn =
    heirShares.length > 1 && maxHeirPct > HEIR_CONCENTRATION_ALERT_PCT;
  const totalTax = heirShares.reduce((s, h) => s + h.tax, 0);

  // Indicateur fiscal (droits de succession) réservé au collectionneur / family
  // office. Masqué pour l'expert et tout futur rôle notaire, tant qu'un retour
  // notaire n'a pas validé l'affichage de cet indicateur.
  const canSeeTax = ["user", "family_office", "admin"].includes(
    userProfile?.role ?? ""
  );

  const fmt = (n: number) =>
    n.toLocaleString(currentLocale === "fr" ? "fr-FR" : "en-US");

  // Datation des valeurs : nombre d'œuvres sans estimation datée + date la plus récente.
  const valuations = artworks.map((a) => getArtworkValuation(a));
  const undatedCount = valuations.filter((v) => v.source !== "estimation").length;
  const latestValuationDate = valuations
    .map((v) => v.date)
    .filter((d): d is string => !!d)
    .sort((a, b) => new Date(b).getTime() - new Date(a).getTime())[0] ?? null;

  const dossierLocale = currentLocale === "fr" ? "fr-FR" : "en-US";

  // Construit les données du dossier (partagées PDF / Excel) — provenance et
  // localisation enrichies à la demande, indicateur fiscal selon le rôle.
  const buildDossierData = async () => {
    const provById: Record<string, { provenance: string; location: string }> = {};
    if (artworks.length > 0) {
      const { data: enrich } = await supabase
        .from("artworks")
        .select("id, acquisitionDetails, location:location_id(name)")
        .in(
          "id",
          artworks.map((a) => a.id)
        );
      (enrich ?? []).forEach((row: any) => {
        let acq = row.acquisitionDetails;
        if (typeof acq === "string") {
          try {
            acq = JSON.parse(acq);
          } catch {
            acq = null;
          }
        }
        let provenance =
          acq?.source ||
          acq?.acquisition_source ||
          acq?.gallery ||
          acq?.auction_house ||
          acq?.seller ||
          "";
        const acqDate = acq?.date || acq?.acquisition_date || "";
        if (provenance && acqDate) provenance += ` (${acqDate})`;
        provById[row.id] = { provenance, location: row.location?.name || "" };
      });
    }

    const items = artworks.map((a, i) => {
      const v = getArtworkValuation(a);
      const heirsStr = mappings
        .filter((m) => m.artwork_id === a.id)
        .map((m) => beneficiaries.find((b) => b.id === m.beneficiary_id)?.name)
        .filter(Boolean)
        .join(", ");
      return {
        n: i + 1,
        title: a.title || t("succession.artwork.untitled"),
        artist: a.artist || t("succession.artwork.artistUnknown"),
        value: v.value,
        dated: v.source === "estimation",
        dateStr: v.date ? fmtDate(v.date) : null,
        heirsStr,
        provenance: provById[a.id]?.provenance || "",
        location: provById[a.id]?.location || "",
      };
    });

    return {
      clientName: userProfile?.first_name || "",
      collectionValue,
      assignedValue,
      unassignedValue,
      coveragePct,
      baseIsGlobal: !!externalEstate,
      baseSource: externalEstate?.source,
      heirs: heirShares.map((h) => ({
        name: h.name,
        regimeLabel: t(`succession.tax.regime.${h.regime}`),
        value: h.value,
        pct: h.pct,
        tax: h.tax,
        exempt: !!TAX_REGIMES[h.regime].exempt,
      })),
      totalTax,
      includeTax: canSeeTax,
      items,
      undatedItems: items.filter((it) => !it.dated),
    };
  };

  const handleExportPdf = async () => {
    setExportingPdf(true);
    try {
      const data = await buildDossierData();
      await generateTransmissionDossierPDF({ t, locale: dossierLocale, data });
    } catch (e) {
      console.error("Erreur génération dossier PDF:", e);
      alert(t("succession.dossier.error"));
    } finally {
      setExportingPdf(false);
    }
  };

  const handleExportXlsx = async () => {
    setExportingXls(true);
    try {
      const data = await buildDossierData();
      await generateTransmissionDossierXLSX({ t, locale: dossierLocale, data });
    } catch (e) {
      console.error("Erreur génération dossier Excel:", e);
      alert(t("succession.dossier.error"));
    } finally {
      setExportingXls(false);
    }
  };

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------
  return (
    <div className="min-h-screen" style={{ background: "#f9f7f4" }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* En-tête */}
        <div className="mb-12">
          <h1 className="text-4xl font-serif font-bold text-gray-900 mb-4">
            {delegateClientId
              ? t("succession.titleDelegate", {
                  defaultValue: "Dossier de succession — {{name}}",
                  name: delegateClientName || "ce client",
                })
              : t("succession.title")}
          </h1>
          <p className="text-lg text-gray-600">
            {delegateClientId
              ? t("succession.subtitleDelegate", {
                  defaultValue: "Consultez et complétez la transmission de la collection de {{name}}.",
                  name: delegateClientName || "ce client",
                })
              : t("succession.subtitle")}
          </p>
        </div>

        {/* Mention légale : souhaits d'organisation, sans valeur testamentaire */}
        <div className="mb-8 flex items-start gap-2 rounded-xl border border-gray-200 bg-gray-50 p-4">
          <AlertCircle className="h-5 w-5 text-gray-500 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-gray-600">{t("succession.legal.notice")}</p>
        </div>

        {/* Erreurs (type simple) */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-red-800">{t(`succession.error.${error}`)}</p>
          </div>
        )}

        {/* Statistiques */}
        <div className="grid md:grid-cols-3 gap-6 mb-12">
          <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
            <div className="flex items-center gap-3 mb-2">
              <Users className="h-6 w-6" style={{ color: "#a9843f" }} />
              <h3 className="text-sm font-medium text-gray-600">
                {t("succession.stats.beneficiaries")}
              </h3>
            </div>
            <p className="text-3xl font-bold text-gray-900">{beneficiaries.length}</p>
          </div>
          <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
            <div className="flex items-center gap-3 mb-2">
              <ImageIcon className="h-6 w-6" style={{ color: "#a9843f" }} />
              <h3 className="text-sm font-medium text-gray-600">
                {t("succession.stats.assignedWorks")}
              </h3>
            </div>
            <p className="text-3xl font-bold text-gray-900">{mappings.length}</p>
          </div>
          <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
            <div className="flex items-center gap-3 mb-2">
              <CheckCircle className="h-6 w-6" style={{ color: "#a9843f" }} />
              <h3 className="text-sm font-medium text-gray-600">
                {t("succession.stats.completed")}
              </h3>
            </div>
            <p className="text-3xl font-bold text-gray-900">
              {getCompletedQuestionnaires()}
            </p>
          </div>
        </div>

        {/* Équilibre de la transmission — synthèse notaire / family office */}
        {(beneficiaries.length > 0 || artworks.length > 0) && (
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm mb-12 p-8">
            <div className="flex items-center gap-3 mb-1">
              <Scale className="h-6 w-6" style={{ color: "#a9843f" }} />
              <h2 className="text-2xl font-serif font-semibold text-gray-900">
                {t("succession.balance.title")}
              </h2>
            </div>
            <p className="text-gray-600 mb-6">{t("succession.balance.subtitle")}</p>

            {/* Répartition de la collection */}
            <div className="grid sm:grid-cols-3 gap-4 mb-6">
              <div className="rounded-xl border border-gray-200 p-4">
                <div className="text-sm text-gray-600">
                  {t("succession.balance.collectionValue")}
                </div>
                <div className="text-2xl font-bold text-gray-900">
                  {fmt(collectionValue)} €
                </div>
              </div>
              <div className="rounded-xl border border-gray-200 p-4">
                <div className="text-sm text-gray-600">
                  {t("succession.balance.assigned")}
                </div>
                <div className="text-2xl font-bold text-gray-900">
                  {fmt(assignedValue)} €
                </div>
              </div>
              <div className="rounded-xl border border-gray-200 p-4">
                <div className="text-sm text-gray-600">
                  {t("succession.balance.unassigned")}
                </div>
                <div className="text-2xl font-bold text-gray-900">
                  {fmt(unassignedValue)} €
                </div>
              </div>
            </div>

            {/* Couverture */}
            <div className="mb-6">
              <div className="h-2.5 w-full rounded-full bg-gray-100 overflow-hidden">
                <div
                  className="h-full"
                  style={{ width: `${coveragePct}%`, background: "#c9a961" }}
                />
              </div>
              <p className="mt-1 text-xs text-gray-500">
                {t("succession.balance.coverage", { pct: coveragePct })}
              </p>
            </div>

            {/* Parts par héritier */}
            {heirShares.length > 0 ? (
              <div className="space-y-3">
                <div className="text-sm font-medium text-gray-700">
                  {t("succession.balance.heirShare")}
                </div>
                {heirShares.map((h) => (
                  <div key={h.id}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-gray-800">{h.name}</span>
                      <span className="font-medium text-gray-900">
                        {fmt(h.value)} € · {h.pct}%{" "}
                        <span className="text-gray-500 font-normal">
                          {externalEstate
                            ? t("succession.balance.ofEstate")
                            : t("succession.balance.ofCollection")}
                        </span>
                      </span>
                    </div>
                    <div className="h-2 w-full rounded-full bg-gray-100 overflow-hidden">
                      <div
                        className="h-full"
                        style={{
                          width: `${Math.min(h.pct, 100)}%`,
                          background: h.pct > HEIR_CONCENTRATION_ALERT_PCT ? "#a9843f" : "#c9a961",
                        }}
                      />
                    </div>
                    {canSeeTax && (
                      <div className="mt-1 flex justify-between text-[11px] text-gray-500">
                        <span>{t(`succession.tax.regime.${h.regime}`)}</span>
                        <span>
                          {TAX_REGIMES[h.regime].exempt
                            ? t("succession.tax.exempt")
                            : `${t("succession.tax.estimatedLabel")} ~ ${fmt(
                                h.tax
                              )} €`}
                        </span>
                      </div>
                    )}
                  </div>
                ))}

                {canSeeTax && (
                  <>
                    <div className="flex justify-between border-t border-gray-100 pt-3 text-sm font-medium text-gray-800">
                      <span>{t("succession.tax.totalLabel")}</span>
                      <span>~ {fmt(totalTax)} €</span>
                    </div>
                    <p className="text-xs text-gray-500 italic">
                      {t("succession.tax.note")}
                    </p>
                  </>
                )}
              </div>
            ) : (
              <p className="text-sm text-gray-500">
                {t("succession.balance.empty")}
              </p>
            )}

            {/* Base de calcul + alerte réserve */}
            <div className="mt-6 space-y-3">
              <p className="text-xs text-gray-500">
                {externalEstate
                  ? t("succession.balance.baseGlobal", {
                      source: externalEstate.source,
                    })
                  : `${t("succession.balance.baseCollectionOnly")} ${t(
                      "succession.balance.connectFO"
                    )}`}
              </p>

              {artworks.length > 0 && (
                <p className="text-xs text-gray-500">
                  {latestValuationDate
                    ? t("succession.balance.valuationDated", {
                        date: fmtDate(latestValuationDate),
                      })
                    : t("succession.balance.valuationNone")}
                  {undatedCount > 0 && (
                    <>
                      {" — "}
                      {t("succession.balance.undatedCount", {
                        count: undatedCount,
                      })}{" "}
                      <button
                        type="button"
                        onClick={() => navigate("/estimation-succession")}
                        className="font-medium hover:underline" style={{ color: "#a9843f" }}
                      >
                        {t("succession.balance.estimateCta")}
                      </button>
                    </>
                  )}
                </p>
              )}

              {heirShares.length > 0 && (
                <div
                  className={`flex items-start gap-2 rounded-xl border p-4 text-sm ${
                    concentrationWarn
                      ? "border-amber-200 bg-amber-50 text-amber-800"
                      : "border-emerald-200 bg-emerald-50 text-emerald-800"
                  }`}
                >
                  {concentrationWarn ? (
                    <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
                  ) : (
                    <CheckCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
                  )}
                  <span>
                    {concentrationWarn
                      ? t("succession.balance.reserveWarn", { pct: maxHeirPct })
                      : t("succession.balance.reserveOk")}
                  </span>
                </div>
              )}

              <p className="text-xs text-gray-500 italic">
                {t("succession.balance.reserveNote")}
              </p>
            </div>

            {artworks.length > 0 && (
              <div className="mt-6 flex flex-wrap gap-3 border-t border-gray-100 pt-5">
                <button
                  type="button"
                  onClick={handleExportPdf}
                  disabled={exportingPdf || exportingXls}
                  className="inline-flex items-center gap-2 rounded-lg bg-gray-900 px-5 py-2.5 text-sm font-medium text-white transition hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <FileDown className="h-4 w-4" />
                  {exportingPdf
                    ? t("succession.dossier.generating")
                    : t("succession.dossier.cta")}
                </button>
                <button
                  type="button"
                  onClick={handleExportXlsx}
                  disabled={exportingPdf || exportingXls}
                  className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-5 py-2.5 text-sm font-medium text-gray-800 transition hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <FileSpreadsheet className="h-4 w-4" />
                  {exportingXls
                    ? t("succession.dossier.generatingXls")
                    : t("succession.dossier.ctaXls")}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Quiz collectionneur (Vision, public) — l'archétype éclaire la transmission.
            Masqué en mode délégué : le quiz porte sur l'archétype du visiteur, pas du client,
            et son résultat n'est pas persisté côté serveur (rien à afficher pour le client ici). */}
        {!delegateClientId && (
          <a
            href={`${VISION_PUBLIC_URL}/quiz-collectionneur`}
            target="_blank"
            rel="noopener noreferrer"
            className="group block mb-12 rounded-2xl p-6 shadow-sm transition hover:shadow-md"
            style={{ background: "#faf6ec", border: "1px solid #e6d9b3" }}
          >
            <div className="flex items-center gap-5">
              <div className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-full" style={{ background: "rgba(201,169,97,0.15)" }}>
                <Palette className="h-6 w-6" style={{ color: "#a9843f" }} />
              </div>
              <div className="flex-1">
                <h3 className="font-serif text-xl font-semibold text-gray-900">
                  {t("succession.quiz.title")}
                </h3>
                <p className="mt-1 text-sm leading-relaxed text-gray-600">
                  {t("succession.quiz.body")}
                </p>
              </div>
              <div className="flex flex-shrink-0 flex-col items-end gap-1">
                <span className="rounded-lg bg-gray-900 px-5 py-2.5 text-sm font-medium text-white transition group-hover:bg-gray-700">
                  {t("succession.quiz.cta")}
                </span>
                <span className="text-xs text-gray-500">{t("succession.quiz.duration")}</span>
              </div>
            </div>
          </a>
        )}

        {/* Contexte */}
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-sm mb-12">
          <div className="p-8">
            <h2 className="text-2xl font-serif font-semibold text-gray-900 mb-6">
              {t("succession.why.title")}
            </h2>
            <div className="space-y-4">
              <p className="text-gray-700 leading-relaxed">
                {t("succession.why.p1")}
              </p>
              <p className="text-gray-700 leading-relaxed">
                {delegateClientId
                  ? t("succession.why.p2Delegate", {
                      defaultValue:
                        "Les œuvres d'art représentent un héritage financier et émotionnel. Une transmission bien orchestrée garantit que les œuvres de {{name}} trouvent leur place auprès des héritiers qui sauront les conserver et en perpétuer l'esprit.",
                      name: delegateClientName || "ce client",
                    })
                  : t("succession.why.p2")}
              </p>
            </div>
          </div>
        </div>

        {/* Contenu selon plan */}
        {getPlanSpecificContent()}

        {/* Bouton ajouter bénéficiaire */}
        <div className="mt-12">
          <button
            onClick={() => setShowAddModal(true)}
            className="w-full group text-white rounded-xl p-6 transition-all duration-200 shadow-lg hover:shadow-xl"
            style={{ background: "#1a1a1a" }}
          >
            <div className="flex items-center justify-center gap-3">
              <Plus className="h-6 w-6" />
              <span className="text-lg font-semibold">
                {t("succession.addBeneficiary")}
              </span>
            </div>
          </button>
        </div>

        {/* Bénéficiaires */}
        {beneficiaries.length > 0 && (
          <div className="mt-12">
            <h2 className="text-2xl font-serif font-semibold text-gray-900 mb-6">
              {t("succession.beneficiaries.title")}
            </h2>
            <div className="grid gap-6 grid-cols-[repeat(auto-fit,minmax(320px,420px))]">
              {beneficiaries.map((beneficiary) => {
                const assignedArtworks = getAssignedArtworks(beneficiary.id);
                const totalValue = assignedArtworks.reduce(
                  (sum, a) => sum + getArtworkValue(a),
                  0
                );
                const status = getHeirStatus({ id: beneficiary.id }, questionnaires, t);
                const RelationIcon = getRelationIcon(beneficiary.relation);
                const initial = (beneficiary.name || "?").trim().charAt(0).toUpperCase();

                return (
                  <div
                    key={beneficiary.id}
                    className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-sm hover:shadow-lg transition-shadow duration-200 flex flex-col"
                  >
                    <div className="p-6">
                      {/* En-tête : avatar + nom + statuts */}
                      <div className="flex items-start gap-4">
                        <div
                          className="flex-shrink-0 h-12 w-12 rounded-full flex items-center justify-center text-lg font-semibold"
                          style={{ background: "#faf6ec", color: "#a9843f", border: "1px solid #e6d9b3" }}
                          aria-hidden="true"
                        >
                          {initial}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-lg font-semibold text-gray-900 truncate">
                            {beneficiary.name}
                          </h3>
                          <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                            <Badge label={beneficiary.relation} tone="gold" icon={RelationIcon} />
                            <Badge {...status} />
                          </div>
                          {beneficiary.email && (
                            <p className="text-xs text-gray-500 mt-1.5 truncate">
                              {beneficiary.email}
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Stats attribution */}
                      <div className="mt-4 flex items-center gap-2">
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-gray-200 bg-gray-50 text-xs font-medium text-gray-700">
                          <Images className="h-3.5 w-3.5 text-gray-400" aria-hidden="true" />
                          {assignedArtworks.length} {t("succession.card.worksLabel")}
                        </span>
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-gray-200 bg-gray-50 text-xs font-medium text-gray-700">
                          <Wallet className="h-3.5 w-3.5 text-gray-400" aria-hidden="true" />
                          {totalValue.toLocaleString(currentLocale === "fr" ? "fr-FR" : "en-US")} €
                        </span>
                      </div>

                      {/* Liens rapides */}
                      <div className="mt-3 flex flex-wrap items-center gap-4">
                        <button
                          type="button"
                          onClick={() => {
                            const targetId = delegateClientId || userProfile?.id;
                            const url = `${VISION_PUBLIC_URL}/matching/validation${
                              targetId ? `?clientId=${encodeURIComponent(targetId)}` : ""
                            }`;
                            window.open(url, "_blank", "noopener,noreferrer");
                          }}
                          className="text-sm text-[#a9843f] hover:underline flex items-center gap-1"
                        >
                          <Eye className="w-4 h-4" aria-hidden="true" />
                          {t("succession.beneficiaries.viewMatching", { defaultValue: "Voir matching" })}
                        </button>
                        <button
                          type="button"
                          onClick={() => openQuestionnaireLink(beneficiary)}
                          className="text-sm text-gray-600 hover:underline flex items-center gap-1"
                        >
                          <Link2 className="w-4 h-4" aria-hidden="true" />
                          {t("succession.beneficiaries.openQuestionnaire", { defaultValue: "Ouvrir questionnaire" })}
                        </button>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-2 mt-5">
                        <button
                          onClick={() => {
                            setSelectedBeneficiary(beneficiary);
                            setShowAssignModal(true);
                          }}
                          className="flex-1 px-4 py-2 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-gray-700 transition-colors flex items-center justify-center gap-2"
                        >
                          <Plus className="h-4 w-4" aria-hidden="true" />
                          {t("succession.assignButton")}
                        </button>
                        <button
                          onClick={() => openEdit(beneficiary)}
                          className="h-9 w-9 flex items-center justify-center border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                          title={t("succession.actions.edit")}
                          aria-label={t("succession.actions.edit")}
                        >
                          <Pencil className="h-4 w-4 text-gray-600" aria-hidden="true" />
                        </button>
                        <button
                          onClick={() => copyQuestionnaireLink(beneficiary)}
                          className="h-9 w-9 flex items-center justify-center border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                          title={t("succession.actions.copyLink")}
                          aria-label={t("succession.actions.copyLink")}
                        >
                          <Link2 className="h-4 w-4 text-gray-600" aria-hidden="true" />
                        </button>
                        <button
                          onClick={() => sendQuestionnaire(beneficiary)}
                          className="h-9 w-9 flex items-center justify-center border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          disabled={!beneficiary.email || sendingEmail}
                          title={
                            !beneficiary.email
                              ? t("succession.actions.missingEmail")
                              : t("succession.actions.sendQuestionnaire")
                          }
                          aria-label={
                            !beneficiary.email
                              ? t("succession.actions.missingEmail")
                              : t("succession.actions.sendQuestionnaire")
                          }
                        >
                          <Mail className="h-4 w-4" style={{ color: "#a9843f" }} aria-hidden="true" />
                        </button>
                        <button
                          onClick={() => copyHeirAccountLink(beneficiary)}
                          className="h-9 w-9 flex items-center justify-center border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          disabled={creatingInviteId === beneficiary.id}
                          title={t("succession.actions.createHeirAccount", {
                            defaultValue: "Créer un accès compte héritier",
                          })}
                          aria-label={t("succession.actions.createHeirAccount", {
                            defaultValue: "Créer un accès compte héritier",
                          })}
                        >
                          <KeyRound className="h-4 w-4" style={{ color: "#a9843f" }} aria-hidden="true" />
                        </button>
                      </div>
                    </div>

                    <div className="mt-auto p-4 bg-gray-50 border-t border-gray-100">
                      {assignedArtworks.length > 0 ? (
                        <div className="space-y-2">
                          {assignedArtworks.map((artwork) => (
                            <div
                              key={artwork.id}
                              className="flex items-center gap-3 bg-white rounded-lg p-3 border border-gray-200"
                            >
                              <img
                                src={getArtImageUrl(artwork)}
                                alt={artwork.title || t("succession.artwork.untitled")}
                                className="w-12 h-12 object-cover rounded flex-shrink-0"
                                onError={(e: any) => {
                                  e.target.src =
                                    "/images/artworks/Vignette_oeuvre_Image_Not_Available.png";
                                }}
                              />
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900 truncate">
                                  {artwork.title || t("succession.artwork.untitled")}
                                </p>
                                <p className="text-xs text-gray-600 truncate">
                                  {artwork.artist || t("succession.artwork.artistUnknown")}
                                </p>
                                <div className="flex items-center gap-1.5 mt-1">
                                  <span className="inline-flex items-center px-1.5 py-0.5 rounded-md bg-gray-100 text-[11px] font-medium text-gray-700">
                                    {getArtworkValue(artwork).toLocaleString(
                                      currentLocale === "fr" ? "fr-FR" : "en-US"
                                    )}{" "}
                                    €
                                  </span>
                                  <span className="text-[11px] text-gray-400 truncate">
                                    {valuationCaption(getArtworkValuation(artwork))}
                                  </span>
                                </div>
                              </div>
                              <button
                                onClick={() => removeAssignment(artwork.id)}
                                className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors flex-shrink-0"
                                title={t("succession.beneficiaries.removeWork")}
                                aria-label={t("succession.beneficiaries.removeWork")}
                              >
                                <X className="h-4 w-4" aria-hidden="true" />
                              </button>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500 text-center py-4">
                          {t("succession.noAssigned")}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Ressources */}
        <div className="mt-12 rounded-2xl p-8" style={{ background: "#faf6ec", border: "1px solid #e6d9b3" }}>
          <h3 className="text-xl font-semibold text-gray-900 mb-4">
            {t("succession.resources.title")}
          </h3>
          <div className="grid md:grid-cols-2 gap-4">
            <button
              onClick={() => navigate("/fiche/dation-paiement")}
              className="flex items-center gap-3 p-4 bg-white rounded-xl hover:shadow-md transition-all duration-200 text-left"
            >
              <BookOpen className="h-6 w-6 flex-shrink-0" style={{ color: "#a9843f" }} />
              <div>
                <div className="font-semibold text-gray-900">
                  {t("succession.resources.dation.title")}
                </div>
                <div className="text-sm text-gray-600">
                  {t("succession.resources.dation.desc")}
                </div>
              </div>
            </button>
            <button
              onClick={() => navigate("/estimation-succession")}
              className="flex items-center gap-3 p-4 bg-white rounded-xl hover:shadow-md transition-all duration-200 text-left"
            >
              <TrendingUp className="h-6 w-6 flex-shrink-0" style={{ color: "#a9843f" }} />
              <div>
                <div className="font-semibold text-gray-900">
                  {t("succession.resources.estimate.title")}
                </div>
                <div className="text-sm text-gray-600">
                  {t("succession.resources.estimate.desc")}
                </div>
              </div>
            </button>
          </div>
        </div>

        {/* Modale ajout bénéficiaire */}
        {showAddModal && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowAddModal(false)}
          >
            <div
              className="bg-white rounded-2xl max-w-lg w-full shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-8">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-2xl font-serif font-semibold text-gray-900">
                    {t("succession.modal.addTitle")}
                  </h3>
                  <button
                    onClick={() => setShowAddModal(false)}
                    className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>

                <form onSubmit={addBeneficiaire} className="space-y-5">
                  {error === "add" && (
                    <div className="rounded-lg bg-red-50 border border-red-200 p-3">
                      <p className="text-sm text-red-800">{t("succession.error.add")}</p>
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t("succession.modal.name")} *
                    </label>
                    <input
                      type="text"
                      value={newBenef.name}
                      onChange={(e) =>
                        setNewBenef({ ...newBenef, name: e.target.value })
                      }
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-400 focus:border-gray-400 transition-colors"
                      placeholder="Marie Dupont"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t("succession.modal.relation")} *
                    </label>
                    <select
                      value={newBenef.relation}
                      onChange={(e) =>
                        setNewBenef({ ...newBenef, relation: e.target.value })
                      }
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-400 focus:border-gray-400 transition-colors"
                    >
                      <option value="Famille">
                        {t("succession.modal.relationFamily")}
                      </option>
                      <option value="Conjoint">
                        {t("succession.modal.relationSpouse")}
                      </option>
                      <option value="Fils">{t("succession.modal.relationSon")}</option>
                      <option value="Fille">
                        {t("succession.modal.relationDaughter")}
                      </option>
                      <option value="Ami">{t("succession.modal.relationFriend")}</option>
                      <option value="Institution">
                        {t("succession.modal.relationInstitution")}
                      </option>
                      <option value="Autre">
                        {t("succession.modal.relationOther")}
                      </option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t("succession.modal.email")}
                    </label>
                    <input
                      type="email"
                      value={newBenef.email}
                      onChange={(e) =>
                        setNewBenef({ ...newBenef, email: e.target.value })
                      }
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-400 focus:border-gray-400 transition-colors"
                      placeholder="marie@example.com"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      {t("succession.modal.emailHelp")}
                    </p>
                  </div>

                  <div className="flex gap-3 pt-4">
                    <button
                      type="button"
                      onClick={() => setShowAddModal(false)}
                      className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
                    >
                      {t("succession.modal.cancel")}
                    </button>
                    <button
                      type="submit"
                      className="flex-1 px-6 py-3 bg-gray-900 text-white rounded-lg font-medium hover:bg-gray-700 transition-colors"
                    >
                      {t("succession.modal.save")}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Modale édition héritier */}
        {showEditModal && editForm && (
          <div
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowEditModal(false)}
          >
            <div
              className="bg-white rounded-2xl max-w-lg w-full shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6 border-b border-gray-100 flex items-center justify-between">
                <h3 className="text-xl font-semibold">
                  {t("succession.modal.editTitle")}
                </h3>
                <button
                  onClick={() => setShowEditModal(false)}
                  className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t("succession.modal.name")}
                  </label>
                  <input
                    type="text"
                    value={editForm.name || ""}
                    onChange={(e) =>
                      setEditForm({ ...editForm, name: e.target.value })
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-400"
                    placeholder="Ex : Marie Dupont"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t("succession.modal.relation")}
                  </label>
                  <input
                    type="text"
                    value={editForm.relation || ""}
                    onChange={(e) =>
                      setEditForm({ ...editForm, relation: e.target.value })
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-400"
                    placeholder={t("succession.modal.relationPlaceholder")}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t("succession.modal.email")}
                  </label>
                  <input
                    type="email"
                    value={editForm.email || ""}
                    onChange={(e) =>
                      setEditForm({ ...editForm, email: e.target.value })
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-400"
                    placeholder="heir@example.com"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {t("succession.modal.emailHelp")}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t("succession.modal.phone")}
                  </label>
                  <input
                    type="text"
                    value={editForm.phone || ""}
                    onChange={(e) =>
                      setEditForm({ ...editForm, phone: e.target.value })
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-400"
                    placeholder="+33 6 12 34 56 78"
                  />
                </div>
              </div>

              <div className="p-6 border-t border-gray-100 bg-gray-50 flex gap-3">
                <button
                  onClick={() => setShowEditModal(false)}
                  className="flex-1 px-4 py-2 border-2 border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-100 transition-colors"
                >
                  {t("succession.modal.cancel")}
                </button>
                <button
                  onClick={async () => {
                    if (!editForm) return;
                    await saveBeneficiary(editForm);
                    setShowEditModal(false);
                  }}
                  className="flex-1 px-4 py-2 bg-gray-900 text-white rounded-lg font-medium hover:bg-gray-700 transition-colors"
                >
                  {t("succession.modal.save")}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modale attribution œuvres */}
        {showAssignModal && selectedBeneficiary && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowAssignModal(false)}
          >
            <div
              className="bg-white rounded-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-8 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-2xl font-serif font-semibold text-gray-900">
                      {t("succession.assign.title", {
                        name: selectedBeneficiary.name,
                      })}
                    </h3>
                    <p className="text-sm text-gray-600 mt-1">
                      {t("succession.assign.subtitle")}
                    </p>
                  </div>
                  <button
                    onClick={() => setShowAssignModal(false)}
                    className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
              </div>

              <div
                className="p-8 overflow-y-auto"
                style={{ maxHeight: "calc(90vh - 180px)" }}
              >
                {unassignedArtworks.length > 0 ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {unassignedArtworks.map((artwork) => {
                      const value = getArtworkValue(artwork);
                      const performance =
                        artwork.price && artwork.price > 0
                          ? Math.round(((value - artwork.price) / artwork.price) * 100)
                          : 0;

                      return (
                        <div
                          key={artwork.id}
                          className="bg-white rounded-lg border-2 border-gray-200 overflow-hidden hover:border-gray-400 transition-all duration-200"
                        >
                          <div className="aspect-square w-full overflow-hidden bg-gray-100">
                            <img
                              src={getArtImageUrl(artwork)}
                              alt={artwork.title || t("succession.artwork.untitled")}
                              className="w-full h-full object-cover"
                              onError={(e: any) => {
                                e.target.src =
                                  "/images/artworks/Vignette_oeuvre_Image_Not_Available.png";
                              }}
                            />
                          </div>
                          <div className="p-4">
                            <h6 className="font-semibold text-gray-900 truncate mb-1">
                              {artwork.title || t("succession.artwork.untitled")}
                            </h6>
                            <p className="text-sm text-gray-600 truncate mb-3">
                              {artwork.artist || t("succession.artwork.artistUnknown")}
                            </p>
                            <div className="space-y-1 mb-4">
                              <p className="text-sm text-gray-700">
                                <span className="font-medium">
                                  {t("succession.assign.purchase")}:
                                </span>{" "}
                                {(artwork.price || 0).toLocaleString(
                                  currentLocale === "fr" ? "fr-FR" : "en-US"
                                )}{" "}
                                €
                              </p>
                              <p className="text-sm text-gray-700">
                                <span className="font-medium">
                                  {t("succession.assign.value")}:
                                </span>{" "}
                                {value.toLocaleString(
                                  currentLocale === "fr" ? "fr-FR" : "en-US"
                                )}{" "}
                                €
                                {performance !== 0 && (
                                  <span
                                    className={`ml-2 text-xs font-medium ${
                                      performance > 0
                                        ? "text-green-600"
                                        : "text-red-600"
                                    }`}
                                  >
                                    {performance > 0 ? "+" : ""}
                                    {performance}%
                                  </span>
                                )}
                              </p>
                              <p className="text-[11px] text-gray-400">
                                {valuationCaption(getArtworkValuation(artwork))}
                              </p>
                            </div>
                            <button
                              onClick={() => assignArtworkTo(artwork.id)}
                              className="w-full px-4 py-2 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-gray-700 transition-colors flex items-center justify-center gap-2"
                            >
                              <Plus className="h-4 w-4" />
                              {t("succession.assign.end")}
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <ImageIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-600 text-lg">
                      {t("succession.assign.allAssigned")}
                    </p>
                  </div>
                )}
              </div>

              <div className="p-6 border-t border-gray-200 bg-gray-50">
                <button
                  onClick={() => setShowAssignModal(false)}
                  className="w-full px-6 py-3 bg-gray-900 text-white rounded-lg font-medium hover:bg-gray-800 transition-colors"
                >
                  {t("succession.assign.close")}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}